import React, {
  MouseEventHandler,
  useState,
  useRef,
  useEffect,
  useMemo
} from "react";
import DOMPurify from "dompurify";
import { arrowUp, arrowDown, close } from "@fdr/static-ui/Icons/icons";
import Icon from "@fdr/static-ui/Icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import JWPlayer, { Player } from "@fdr/atomic-ui/_atoms/JWPlayer";
import { JWPlayerAPITracks } from "@fdr/video-builder/src/types";
import { NullaryFn, UnaryFn } from "@tvg/ts-types/Functional";
import { isXSell, openExternalLink } from "@tvg/sh-utils/mobileUtils";
import {
  Container,
  PlayerWrapper,
  CloseButton,
  ReadingWrapper,
  ClickableHeader,
  Title,
  Description,
  Timestamp
} from "./styled-components";

interface Props {
  src: string;
  library: string;
  title: string;
  description?: string;
  uploadedAt: string;
  tracks?: JWPlayerAPITracks[];
  isAutoPlay?: boolean;
  renderCaptionsNatively?: boolean;
  allowFullscreen?: boolean;
  qaLabel?: string;
  onClose?: MouseEventHandler<HTMLButtonElement>;
  height?: number;
  width?: number;
  onPause?: NullaryFn<void>;
  onPlay?: UnaryFn<boolean, void>;
  onVideoViewed?: NullaryFn<void>;
  onVideoPlayedCompletly?: NullaryFn<void>;
  onVideoPlayAttemptFailed?: UnaryFn<string, void>;
  onSetupError?: UnaryFn<string, void>;
  onVideoMuteAndUnmute?: UnaryFn<boolean, void>;
  onVideoFullScreen?: UnaryFn<boolean, void>;
  deviceLockRotationToggle: UnaryFn<boolean, void>;
}

const VideoPlayerDetails = ({
  src,
  library,
  title,
  description,
  uploadedAt,
  tracks,
  isAutoPlay = false,
  renderCaptionsNatively = false,
  allowFullscreen = false,
  qaLabel = "video-details",
  onClose,
  height = 220,
  width = 390,
  onPause,
  onPlay,
  onVideoViewed,
  onVideoPlayedCompletly,
  onVideoPlayAttemptFailed,
  onSetupError,
  onVideoMuteAndUnmute,
  onVideoFullScreen,
  deviceLockRotationToggle
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [expanded, setExpanded] = useState(true);
  const [videoPlayer, setVideoPlayer] = useState<Player>();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const attachEvents = (vPlayer: Player) => {
    vPlayer.on("pause", () => onPause && onPause());
    vPlayer.on("play", (e) => onPlay && onPlay(!!e.viewable));
    vPlayer.on(
      "complete",
      () => onVideoPlayedCompletly && onVideoPlayedCompletly()
    );
    vPlayer.on(
      "error",
      (e) => onVideoPlayAttemptFailed && onVideoPlayAttemptFailed(e.message)
    );
    vPlayer.on("setupError", (e) => onSetupError && onSetupError(e.message));
    vPlayer.on("ready", () => onVideoViewed && onVideoViewed());
    vPlayer.on(
      "mute",
      (mute) => onVideoMuteAndUnmute && onVideoMuteAndUnmute(!!mute.mute)
    );
    vPlayer.on("fullscreen", (fullscreen) => {
      setIsFullscreen(!!fullscreen.fullscreen);

      if (onVideoFullScreen) {
        onVideoFullScreen(!!fullscreen.fullscreen);
      }
    });
  };

  const detachEvents = (vPlayer: Player) => {
    vPlayer.off("pause", () => onPause && onPause());
    vPlayer.off("play", (e) => onPlay && onPlay(!!e.viewable));
    vPlayer.off(
      "complete",
      () => onVideoPlayedCompletly && onVideoPlayedCompletly()
    );
    vPlayer.off(
      "error",
      (e) => onVideoPlayAttemptFailed && onVideoPlayAttemptFailed(e.message)
    );
    vPlayer.off("setupError", (e) => onSetupError && onSetupError(e.message));
    vPlayer.off("ready", () => onVideoViewed && onVideoViewed());
    vPlayer.off(
      "mute",
      (mute) => onVideoMuteAndUnmute && onVideoMuteAndUnmute(!!mute)
    );
    vPlayer.off(
      "fullscreen",
      (fullscreen) => onVideoFullScreen && onVideoFullScreen(!!fullscreen)
    );
  };

  const resizeVideoToWrapper = () => {
    if (wrapperRef.current && videoPlayer) {
      const { width: wrapperWidth, height: wrapperHeight } =
        wrapperRef.current.getBoundingClientRect();
      videoPlayer.resize(wrapperWidth, wrapperHeight);
    }
  };

  useEffect(() => {
    if (videoPlayer) {
      attachEvents(videoPlayer);
    }

    return () => {
      if (videoPlayer) detachEvents(videoPlayer);
    };
  }, [videoPlayer]);

  useEffect(() => {
    resizeVideoToWrapper();
  }, [wrapperRef.current, videoPlayer, expanded, isFullscreen]);

  const orientationChange = () => {
    const isLandscape = !window.matchMedia("(orientation: portrait)").matches;
    if (videoPlayer?.getState() === "playing") {
      setIsFullscreen(isLandscape);
      videoPlayer?.setFullscreen(isLandscape);
    }
    resizeVideoToWrapper();
  };

  useEffect(() => {
    if (window) {
      deviceLockRotationToggle(false);
      // Only to clean de listener if already exists
      window.removeEventListener("resize", orientationChange);
      window.addEventListener("resize", orientationChange);
    }

    return () => {
      window.removeEventListener("resize", orientationChange);
      deviceLockRotationToggle(true);
    };
  }, [videoPlayer]);

  const parseWithTarget = (desc: string) => {
    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
      if ("target" in node) {
        node.setAttribute("id", node.getAttribute("href") || "");
        setTimeout(() => {
          const element = document.getElementById(
            node.getAttribute("href") || ""
          );
          if (element)
            element.addEventListener("click", (e) => {
              e.preventDefault();
              e.stopPropagation();
              openExternalLink(element.getAttribute("href") || "");
            });
        }, 50);
      }
    });

    return DOMPurify.sanitize(desc);
  };

  return useMemo(
    () => (
      <Container data-qa-label={qaLabel}>
        <PlayerWrapper
          ref={wrapperRef}
          data-qa-label={`${qaLabel}-player-wrapper`}
          height={height}
        >
          {onClose && (
            <CloseButton
              isXSell={isXSell()}
              data-qa-label={`${qaLabel}-close`}
              onClick={onClose}
            >
              <Icon icon={close} color={buildColor("white")} size={16} />
            </CloseButton>
          )}
          {src && library && (
            <JWPlayer
              src={src}
              tracks={tracks}
              library={library}
              isAutoPlay={isAutoPlay}
              renderCaptionsNatively={renderCaptionsNatively}
              allowFullscreen={allowFullscreen}
              didMountCallback={({ player }) => setVideoPlayer(player)}
              height={height}
              width={width}
            />
          )}
        </PlayerWrapper>
        {!isFullscreen && (
          <ReadingWrapper titleOnly={!description}>
            <ClickableHeader
              role="button"
              onClick={() => setExpanded(!expanded)}
            >
              <Title data-qa-label={`${qaLabel}-title`}>{title}</Title>
              {description && (
                <Icon
                  icon={expanded ? arrowDown : arrowUp}
                  color={buildColor("grey", "D4")}
                  size={24}
                />
              )}
            </ClickableHeader>
            {description && expanded && (
              <Description
                data-qa-label={`${qaLabel}-description`}
                dangerouslySetInnerHTML={{
                  __html: parseWithTarget(description)
                }}
              />
            )}
            <Timestamp data-qa-label={`${qaLabel}-timestamp`}>
              {uploadedAt}
            </Timestamp>
          </ReadingWrapper>
        )}
      </Container>
    ),
    [expanded, src, library, title, description, uploadedAt, isFullscreen]
  );
};

export default VideoPlayerDetails;
